/**
 * Helpers for showing a Drivelo instructor listing: the car, the suburb and labels for listing
 * field options. Listing fields are configured in Console; these read them by key.
 */

// Console listing field keys for the instructor's teaching car. All optional: nothing about the
// car is shown until the fields exist in Console and the instructor fills them in.
export const VEHICLE_FIELD_KEYS = {
  make: 'vehicle_make',
  model: 'vehicle_model',
  year: 'vehicle_year',
  ancap: 'vehicle_ancap',
};

// Other Console listing field keys the profile and search card place in their own spots
export const LESSON_FOCUS_KEY = 'Lesson_Focus';
export const TRANSMISSION_KEY = 'TransmissionType';
export const PICKUP_KEY = 'Pickup';
export const SUBURBS_KEY = 'Suburbsserviced';
export const ABOUT_LESSON_KEY = 'About_Lesson';
// Optional Console multi-enum field: the languages the instructor can teach in
export const LANGUAGES_KEY = 'Languages';

export const INSTRUCTOR_FIELD_KEYS = [
  LESSON_FOCUS_KEY,
  TRANSMISSION_KEY,
  PICKUP_KEY,
  SUBURBS_KEY,
  ABOUT_LESSON_KEY,
  LANGUAGES_KEY,
  ...Object.values(VEHICLE_FIELD_KEYS),
];

const isFilled = value =>
  (typeof value === 'string' && value.trim().length > 0) || typeof value === 'number';

/**
 * Read the teaching car from a listing's public data.
 *
 * @param {Object} publicData listing public data
 * @returns {{ make: string?, model: string?, year: number?, ancap: string? }|null} null when
 * nothing about the car is filled in
 */
export const getVehicle = publicData => {
  const data = publicData || {};
  const vehicle = Object.entries(VEHICLE_FIELD_KEYS).reduce((acc, [name, key]) => {
    const value = data[key];
    return isFilled(value)
      ? { ...acc, [name]: typeof value === 'string' ? value.trim() : value }
      : acc;
  }, {});
  return Object.keys(vehicle).length > 0 ? vehicle : null;
};

/**
 * Year, make and model in one line, e.g. "2022 Toyota Corolla".
 *
 * @param {Object|null} vehicle from getVehicle
 * @returns {string|null}
 */
export const formatVehicleName = vehicle => {
  const parts = [vehicle?.year, vehicle?.make, vehicle?.model].filter(isFilled);
  return parts.length > 0 ? parts.join(' ') : null;
};

/**
 * Labels for the selected options of an enum or multi-enum listing field, in config order.
 *
 * @param {Array<Object>} listingFields config.listing.listingFields
 * @param {string} key listing field key
 * @param {string|Array<string>} selected selected option(s) from public data
 * @returns {Array<{ option: string, label: string }>}
 */
export const getOptionLabels = (listingFields, key, selected) => {
  const values = Array.isArray(selected) ? selected : selected != null ? [selected] : [];
  if (values.length === 0) {
    return [];
  }
  const fieldConfig = (listingFields || []).find(field => field?.key === key);
  const options = fieldConfig?.enumOptions || [];
  const known = options
    .filter(({ option }) => values.includes(option))
    .map(({ option, label }) => ({ option, label: label || option }));
  // Keep values that are no longer in the config, so nothing silently disappears
  const unknown = values
    .filter(value => !options.some(({ option }) => option === value))
    .map(value => ({ option: value, label: value }));
  return [...known, ...unknown];
};

/**
 * The listing's address without the country, e.g. "Geelong VIC".
 *
 * @param {Object} publicData listing public data
 * @returns {string|null}
 */
export const getShortAddress = publicData => {
  const address = publicData?.location?.address;
  if (typeof address !== 'string' || address.trim().length === 0) {
    return null;
  }
  return address.replace(/,\s*Australia\s*$/i, '').trim();
};
